import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from '@/lib/sonner'

export interface Notification {
  id: string
  type: string
  title: string
  content: string
  module: string
  eventType: string
  isRead: boolean
  readAt?: string | null
  relatedOrderId?: string | null
  relatedOrder?: {
    id: string
    orderNo: string
  } | null
  createdAt: string
}

export interface NotificationListParams {
  isRead?: boolean
  type?: string
  module?: string
  eventType?: string
  page?: number
  limit?: number
}

export interface NotificationListResponse {
  data: Notification[]
  total: number
  page: number
  limit: number
  statistics: {
    total: number
    unread: number
    read: number
  }
}

export function useNotifications(params?: NotificationListParams) {
  const queryParams = new URLSearchParams()

  if (params?.isRead !== undefined) queryParams.append('isRead', params.isRead.toString())
  if (params?.type) queryParams.append('type', params.type)
  if (params?.module) queryParams.append('module', params.module)
  if (params?.eventType) queryParams.append('eventType', params.eventType)
  if (params?.page) queryParams.append('page', params.page.toString())
  if (params?.limit) queryParams.append('limit', params.limit.toString())

  return useQuery<NotificationListResponse>({
    queryKey: ['notifications', params],
    queryFn: async () => {
      const url = queryParams.toString()
        ? `/api/v1/notifications?${queryParams.toString()}`
        : '/api/v1/notifications'

      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('access_token')}`,
        },
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || '获取通知列表失败')
      }

      const result = await response.json()
      return result.result
    },
    staleTime: 30000,
    retry: 1,
  })
}

export function useMarkNotificationRead() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (notificationId: string) => {
      const response = await fetch(`/api/v1/notifications/${notificationId}/read`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('access_token')}`,
        },
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || '标记已读失败')
      }

      return response.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
    },
    onError: (error: Error) => {
      toast.error(error.message)
    },
  })
}

export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      const response = await fetch('/api/v1/notifications/mark-all-read', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('access_token')}`,
        },
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || '批量标记已读失败')
      }

      return response.json()
    },
    onSuccess: (jsonResponse) => {
      const count = jsonResponse.result?.count || 0
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
      if (count > 0) {
        toast.success(`已将 ${count} 条通知标记为已读`)
      } else {
        toast.info('没有未读通知')
      }
    },
    onError: (error: Error) => {
      toast.error(error.message)
    },
  })
}
